import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../services/api';
import { useTranslation } from '../hooks/useTranslation';

interface BotData {
  id: string;
  packageKey: string;
  packageName: string;
  packageFee: number;
  lockedBalance: number;
  rewardAmount: number;
  opportunityInterval: number;
  durationDays: number;
  daysRemaining: number;
  status: string;
  consecutiveMissed: number;
  isLocked: boolean;
  nextOpportunityAt: string | null;
  pausedAt: string | null;
  resumeAt: string | null;
  perfectBonusTotal: number;
  perfectDaysCount: number;
  perfectBonusAmount: number;
  luckyEligible: boolean;
  luckyBonusAmount: number;
  lastCatchAt: string | null;
  giftsSentThisMonth: number;
  giftMonthlyLimit: number;
  startedAt: string;
  endsAt: string;
}

interface OpportunityData {
  id: string;
  status: string;
  rewardAmount: number;
  opensAt: string;
  expiresAt: string;
  caughtAt: string | null;
  rewardDeadline: string | null;
  rewardedAt: string | null;
  signalPair: string | null;
  signalDirection: string | null;
  signalEntry: string | null;
  signalTp: string | null;
  signalSl: string | null;
  signalExchange: string | null;
}

interface PackageConfig {
  key: string;
  name: string;
  packageFee: number;
  requiredBalance: number;
  opportunityIntervalHours: number;
  rewardAmount: number;
  durationDays: number;
  giftMonthlyLimit: number;
}

interface StatusData {
  hasBot: boolean;
  botBalance: number;
  usdtBalance: number;
  bot: BotData | null;
  currentOpportunity: OpportunityData | null;
  packages: PackageConfig[];
}

interface HistoryItem {
  id: string;
  kind: string;
  title: string;
  amount: number | null;
  sign: '+' | '-' | '';
  status: string | null;
  date: string;
  detail?: string | null;
}

interface GiftRecipient {
  found: boolean;
  eligible: boolean;
  username?: string;
  displayName?: string;
  reason?: string;
}

const PKG_COLORS: Record<string, string> = {
  new_trader: '#10b981',
  adventurer: '#3b82f6',
  prof_trader: '#8b5cf6',
  elite_trader: '#f59e0b',
};

const PERFECT_BONUS = 1;
const LUCKY_RATE = 0.1;

const muted = 'rgba(255,255,255,0.5)';
const faint = 'rgba(255,255,255,0.4)';

const s = {
  page: { minHeight: '100vh', background: 'linear-gradient(180deg, #0a1628 0%, #000814 100%)', color: '#fff', paddingBottom: '100px' } as React.CSSProperties,
  card: { background: 'rgba(0,0,0,0.3)', borderRadius: '14px', padding: '16px', marginBottom: '12px', border: '1px solid rgba(255,255,255,0.06)' } as React.CSSProperties,
  btn: (active: boolean, color: string) => ({
    width: '100%', padding: '14px', border: 'none', borderRadius: '12px', color: '#fff', fontSize: '16px', fontWeight: 700,
    cursor: active ? 'pointer' : 'not-allowed',
    background: active ? `linear-gradient(135deg, ${color} 0%, ${color}cc 100%)` : 'rgba(255,255,255,0.1)',
    opacity: active ? 1 : 0.5,
  } as React.CSSProperties),
  input: { width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', padding: '12px', color: '#fff', fontSize: '16px', outline: 'none', boxSizing: 'border-box' as const } as React.CSSProperties,
  row: { display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' } as React.CSSProperties,
};

function fmtHms(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const sec = total % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
}

function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  return `${d.getDate().toString().padStart(2, '0')}.${(d.getMonth() + 1).toString().padStart(2, '0')}.${d.getFullYear()} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
}

function Header({ title, subtitle, onBack }: { title: string; subtitle?: string; onBack?: () => void }) {
  return (
    <div style={{ padding: '16px 16px 12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {onBack && (
          <button onClick={onBack} aria-label="Back" style={{
            background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: '10px',
            color: '#fff', fontSize: '14px', fontWeight: 600, cursor: 'pointer', padding: '8px 12px', flexShrink: 0,
          }}>
            ← Back
          </button>
        )}
        <h1 style={{ fontSize: '20px', fontWeight: 800, margin: 0, flex: 1, textAlign: onBack ? 'left' : 'center' }}>{title}</h1>
      </div>
      {subtitle && <div style={{ fontSize: '12px', color: muted, marginTop: '6px', textAlign: onBack ? 'left' : 'center' }}>{subtitle}</div>}
    </div>
  );
}

function CopyField({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = value;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const color = highlight ? '#f59e0b' : '#3b82f6';
  return (
    <div style={{ marginBottom: '14px' }}>
      <div style={{ fontSize: '13px', color: muted, marginBottom: '4px' }}>{label}</div>
      <div style={{
        background: highlight ? 'rgba(245,158,11,0.1)' : 'rgba(255,255,255,0.05)', borderRadius: '8px', padding: '10px',
        fontSize: highlight ? '16px' : '13px', fontWeight: highlight ? 700 : 400, color: highlight ? '#f59e0b' : '#fff',
        wordBreak: 'break-all', marginBottom: '6px', textAlign: highlight ? 'center' : 'left',
      }}>
        {value}
      </div>
      <button onClick={copy} style={{ ...s.btn(true, copied ? '#10b981' : color), padding: '9px', fontSize: '13px' }}>
        {copied ? 'Copied ✓' : `Copy ${label.replace(/\s*\(.*\)/, '')}`}
      </button>
    </div>
  );
}

function SignalBlock({ opp }: { opp: OpportunityData }) {
  const { t } = useTranslation();
  const disclaimer = (
    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.45)', marginTop: '10px', textAlign: 'center', lineHeight: 1.4 }}>
      ⚠️ {t('aiTrader.signal_risk')}
    </div>
  );
  if (!opp.signalPair) {
    return (
      <div style={{ marginTop: '12px', background: 'rgba(0,0,0,0.25)', borderRadius: '10px', padding: '14px', textAlign: 'center' }}>
        <div style={{ fontSize: '20px', fontWeight: 700 }}>Good Luck! 🍀</div>
      </div>
    );
  }
  const dirColor = opp.signalDirection === 'LONG' ? '#10b981' : '#ef4444';
  return (
    <div style={{ marginTop: '12px', background: 'rgba(0,0,0,0.3)', borderRadius: '10px', padding: '12px' }}>
      <div style={{ fontSize: '12px', fontWeight: 700, marginBottom: '8px', color: '#3b82f6' }}>📊 AI Trader Signal</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', fontSize: '12px' }}>
        <div><span style={{ color: faint }}>Pair</span><div style={{ fontWeight: 700 }}>{opp.signalPair}</div></div>
        <div><span style={{ color: faint }}>Direction</span><div style={{ fontWeight: 700, color: dirColor }}>{opp.signalDirection}</div></div>
        <div><span style={{ color: faint }}>Entry</span><div style={{ fontWeight: 700 }}>{opp.signalEntry}</div></div>
        <div><span style={{ color: faint }}>Take Profit</span><div style={{ fontWeight: 700, color: '#10b981' }}>{opp.signalTp}</div></div>
        <div><span style={{ color: faint }}>Stop Loss</span><div style={{ fontWeight: 700, color: '#ef4444' }}>{opp.signalSl}</div></div>
      </div>
      {opp.signalExchange && (
        <button onClick={() => {
          const tg = (window as any).Telegram?.WebApp;
          if (tg?.openLink) tg.openLink(opp.signalExchange!); else window.open(opp.signalExchange!, '_blank');
        }} style={{ ...s.btn(true, '#3b82f6'), marginTop: '10px', padding: '8px', fontSize: '12px' }}>
          Open Exchange
        </button>
      )}
      {disclaimer}
    </div>
  );
}

function HistoryView({ onBack }: { onBack: () => void }) {
  const [items, setItems] = useState<HistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.getAiTraderHistory()
      .then(res => setItems(res.data.data || []))
      .catch((e: any) => setError(e.response?.data?.message || 'Failed to load history'))
      .finally(() => setLoading(false));
  }, []);

  const kindIcon: Record<string, string> = {
    reward: '💰', opportunity: '🎯', gift: '🎁', bonus: '⭐', deposit: '📥', purchase: '🤖', transfer: '🔄', system: '⚙️',
  };
  const statusColor: Record<string, string> = {
    missed: '#ef4444', lost: '#ef4444', reward_pending: '#f59e0b', open: '#3b82f6', rewarded: '#10b981', sent: '#10b981', received: '#10b981', completed: '#10b981',
  };

  return (
    <div style={s.page}>
      <Header title="AI Trader History" subtitle="Opportunities, rewards, gifts, bonuses and deposits" onBack={onBack} />
      <div style={{ padding: '0 16px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: faint }}>Loading...</div>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#ef4444' }}>{error}</div>
        ) : items.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: faint }}>No activity yet</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {items.map(item => {
              const amountColor = item.sign === '+' ? '#10b981' : item.sign === '-' ? '#ef4444' : 'rgba(255,255,255,0.7)';
              const decimals = item.amount !== null && item.amount < 1 && item.amount > 0 ? 5 : 2;
              return (
                <div key={item.id} style={{ ...s.card, marginBottom: 0, padding: '12px 14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: 700 }}>{kindIcon[item.kind] || '•'} {item.title}</div>
                      <div style={{ fontSize: '11px', color: faint, marginTop: '2px' }}>{fmtDateTime(item.date)}</div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      {item.amount !== null && (
                        <div style={{ fontSize: '14px', fontWeight: 800, color: amountColor, fontVariantNumeric: 'tabular-nums' }}>
                          {item.sign}${item.amount.toFixed(decimals)}
                        </div>
                      )}
                      {item.status && (
                        <div style={{ fontSize: '10px', fontWeight: 700, color: statusColor[item.status] || faint, marginTop: '2px' }}>
                          {item.status.replace('_', ' ').toUpperCase()}
                        </div>
                      )}
                    </div>
                  </div>
                  {item.detail && <div style={{ fontSize: '11px', color: faint, marginTop: '4px' }}>{item.detail}</div>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

type View = 'main' | 'packages' | 'deposit' | 'gift' | 'history' | 'transfer';

export function AiTraderScreen() {
  const { t } = useTranslation();
  const [data, setData] = useState<StatusData | null>(null);
  const [view, setView] = useState<View>('main');
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [now, setNow] = useState(Date.now());

  const [riskAccepted, setRiskAccepted] = useState(false);
  const [giftUsername, setGiftUsername] = useState('');
  const [giftRecipient, setGiftRecipient] = useState<GiftRecipient | null>(null);
  const [giftChecking, setGiftChecking] = useState(false);
  const [giftResult, setGiftResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [transferAmount, setTransferAmount] = useState('');
  const [depositInfo, setDepositInfo] = useState<{ walletAddress: string; memo: string } | null>(null);
  const refreshingRef = useRef(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.getAiTraderStatus();
      setData(res.data.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Refresh when a deadline passes so the server-side state (cron) is picked up
  useEffect(() => {
    if (!data?.bot) return;
    const opp = data.currentOpportunity;
    const targets = [
      opp?.status === 'open' ? opp.expiresAt : null,
      opp?.status === 'reward_pending' ? opp.rewardDeadline : null,
      data.bot.nextOpportunityAt,
      data.bot.resumeAt,
    ].filter(Boolean) as string[];
    const due = targets.some(iso => {
      const diff = new Date(iso).getTime() - now;
      return diff <= 0 && diff > -120000;
    });
    if (due && !refreshingRef.current && now % 15000 < 1000) {
      refreshingRef.current = true;
      fetchData().finally(() => { refreshingRef.current = false; });
    }
  }, [now, data, fetchData]);

  // Telegram native back button inside AI Trader sub-views
  useEffect(() => {
    const tg = (window as any).Telegram?.WebApp;
    if (!tg?.BackButton) return;
    if (view === 'main') return;
    const handler = () => setView('main');
    try {
      tg.BackButton.show();
      tg.BackButton.onClick(handler);
    } catch { /* ignore */ }
    return () => {
      try {
        tg.BackButton.offClick(handler);
        tg.BackButton.hide();
      } catch { /* ignore */ }
    };
  }, [view]);

  // Debounced gift recipient lookup
  useEffect(() => {
    setGiftResult(null);
    const name = giftUsername.trim().replace(/^@/, '');
    if (name.length < 3) { setGiftRecipient(null); setGiftChecking(false); return; }
    setGiftChecking(true);
    const id = setTimeout(() => {
      api.lookupAiTraderGiftRecipient(name)
        .then(res => setGiftRecipient(res.data.data))
        .catch(() => setGiftRecipient({ found: false, eligible: false, reason: 'Lookup failed' }))
        .finally(() => setGiftChecking(false));
    }, 500);
    return () => clearTimeout(id);
  }, [giftUsername]);

  const goTo = (v: View) => {
    window.scrollTo(0, 0);
    setView(v);
  };

  const act = async (fn: () => Promise<any>) => {
    if (acting) return;
    setActing(true);
    try {
      await fn();
      await fetchData();
    } catch (e: any) {
      alert(e.response?.data?.message || 'Error');
    } finally {
      setActing(false);
    }
  };

  const handleCatch = () => {
    if (!data?.currentOpportunity) return;
    act(() => api.catchAiTraderOpportunity(data.currentOpportunity!.id));
  };

  const handleTransferReward = () => {
    if (!data?.currentOpportunity) return;
    act(() => api.transferAiTraderReward(data.currentOpportunity!.id));
  };

  const handlePurchase = (packageKey: string) => act(async () => { await api.purchaseAiTraderBot(packageKey); goTo('main'); });
  const handleUpgrade = (packageKey: string) => act(async () => { await api.upgradeAiTraderBot(packageKey); goTo('main'); });
  const handleResume = () => act(() => api.resumeAiTraderBot());
  const handleAcknowledgeLock = () => act(() => api.acknowledgeAiTraderLock());
  const handleTransferToMain = () => {
    if (!transferAmount) return;
    act(async () => { await api.transferBotBalanceToMain(Number(transferAmount)); setTransferAmount(''); goTo('main'); });
  };

  const handleSendGift = async () => {
    if (!giftRecipient?.eligible || acting) return;
    const target = giftRecipient.username || giftUsername.trim().replace(/^@/, '');
    if (!confirm(`Send $20 USDT gift to ${giftRecipient.displayName || ''} (@${target})?`)) return;
    setActing(true);
    setGiftResult(null);
    try {
      await api.sendAiTraderGift(target);
      setGiftResult({ ok: true, message: `$20 USDT sent to ${giftRecipient.displayName || ''} (@${target})` });
      setGiftUsername('');
      await fetchData();
    } catch (e: any) {
      setGiftResult({ ok: false, message: e.response?.data?.message || 'Gift could not be sent' });
    } finally {
      setActing(false);
    }
  };

  const loadDepositInfo = useCallback(async () => {
    try {
      const res = await api.getAiTraderDepositInfo();
      setDepositInfo(res.data.data);
    } catch (e) { console.error(e); }
  }, []);

  const msUntil = (iso: string | null) => (iso ? new Date(iso).getTime() - now : 0);

  if (loading) return <div style={s.page}><div style={{ padding: '40px', textAlign: 'center', color: muted }}>Loading...</div></div>;
  if (!data) return <div style={s.page}><div style={{ padding: '40px', textAlign: 'center', color: muted }}>Failed to load</div></div>;

  if (view === 'history') return <HistoryView onBack={() => goTo('main')} />;
  if (view === 'packages') return renderPackages();
  if (view === 'deposit') return renderDeposit();
  if (view === 'gift') return renderGift();
  if (view === 'transfer') return renderTransfer();
  return renderMain();

  function renderNextCountdown(bot: BotData) {
    if (!bot.nextOpportunityAt) return null;
    const ms = msUntil(bot.nextOpportunityAt);
    return (
      <div style={{ marginTop: '10px' }}>
        <div style={{ fontSize: '12px', color: muted }}>Next Opportunity in</div>
        <div style={{ fontSize: '24px', fontWeight: 800, color: '#3b82f6', fontVariantNumeric: 'tabular-nums' }}>
          {ms > 0 ? fmtHms(ms) : 'Opening…'}
        </div>
      </div>
    );
  }

  function renderMain() {
    const bot = data!.bot;
    const opp = data!.currentOpportunity;
    const hasLiveOpp = opp && (opp.status === 'open' || opp.status === 'reward_pending');

    return (
      <div style={s.page}>
        <Header title="🤖 AI Trader" subtitle="Automated USDT trading bot" />

        <div style={{ padding: '0 16px' }}>
          <div style={{ ...s.card, border: '1px solid rgba(45,125,50,0.3)', background: 'rgba(45,125,50,0.06)' }}>
            <div style={{ fontSize: '12px', color: muted }}>Bot Balance</div>
            <div style={{ fontSize: '28px', fontWeight: 800, color: '#2e7d32' }}>${data!.botBalance.toFixed(2)}</div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
              <button onClick={() => { goTo('deposit'); loadDepositInfo(); }} style={{ ...s.btn(true, '#3b82f6'), flex: 1, padding: '10px', fontSize: '13px' }}>
                Deposit
              </button>
              {bot?.status === 'expired' && (
                <button onClick={() => goTo('transfer')} style={{ ...s.btn(data!.botBalance > 0, '#f59e0b'), flex: 1, padding: '10px', fontSize: '13px' }}>
                  → Main
                </button>
              )}
              <button onClick={() => goTo('history')} style={{ ...s.btn(true, '#6b7280'), flex: 1, padding: '10px', fontSize: '13px' }}>
                History
              </button>
            </div>
          </div>

          {!bot && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>🤖</div>
              <div style={{ fontSize: '16px', fontWeight: 700, marginBottom: '8px' }}>Start AI Trading</div>
              <p style={{ fontSize: '13px', color: muted, marginBottom: '16px' }}>Purchase a bot package to start earning USDT</p>
              <button onClick={() => goTo('packages')} style={s.btn(true, '#3b82f6')}>Choose a Bot</button>
            </div>
          )}

          {bot && bot.status !== 'expired' && (
            <>
              {renderBotCard(bot)}

              {bot.status === 'pause' && renderPauseSection(bot)}
              {bot.status !== 'pause' && bot.isLocked && renderLockedSection()}
              {bot.status === 'active' && !bot.isLocked && opp && renderOpportunitySection(opp, bot)}
              {bot.status === 'active' && !bot.isLocked && !hasLiveOpp && !opp && (
                <div style={{ ...s.card, textAlign: 'center', border: '1px solid rgba(59,130,246,0.2)' }}>
                  {renderNextCountdown(bot) || <div style={{ fontSize: '13px', color: muted }}>Preparing your first opportunity…</div>}
                </div>
              )}

              <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                <button onClick={() => goTo('packages')} style={{ ...s.btn(true, '#8b5cf6'), flex: 1, padding: '10px', fontSize: '13px' }}>
                  Upgrade
                </button>
                <button onClick={() => goTo('gift')} disabled={bot.status !== 'active'} style={{ ...s.btn(bot.status === 'active', '#10b981'), flex: 1, padding: '10px', fontSize: '13px' }}>
                  Gift
                </button>
              </div>

              {renderBonuses(bot)}
            </>
          )}

          {bot && bot.status === 'expired' && (
            <div style={{ ...s.card, border: '1px solid rgba(239,68,68,0.3)', textAlign: 'center' }}>
              <div style={{ fontSize: '14px', fontWeight: 700, color: '#ef4444', marginBottom: '8px' }}>Bot Expired</div>
              <p style={{ fontSize: '12px', color: muted, marginBottom: '12px' }}>
                Your {bot.packageName} bot has expired. Transfer your Bot Balance to Main Balance, or purchase a new bot.
              </p>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={() => goTo('transfer')} disabled={data!.botBalance <= 0} style={{ ...s.btn(data!.botBalance > 0, '#f59e0b'), flex: 1 }}>
                  Transfer to Main
                </button>
                <button onClick={() => goTo('packages')} style={{ ...s.btn(true, '#3b82f6'), flex: 1 }}>New Bot</button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  function renderBotCard(bot: BotData) {
    const color = PKG_COLORS[bot.packageKey] || '#888';
    const statusColors: Record<string, string> = { active: '#10b981', pause: '#f59e0b', expired: '#ef4444' };
    const statusLabel = bot.isLocked && bot.status === 'active' ? 'LOCKED' : bot.status.toUpperCase();
    const statusColor = bot.isLocked && bot.status === 'active' ? '#ef4444' : statusColors[bot.status] || '#888';
    return (
      <div style={{ ...s.card, borderColor: `${color}33` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <span style={{ fontSize: '18px', fontWeight: 800, color }}>🤖 {bot.packageName}</span>
          <span style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '10px', fontWeight: 700, color: statusColor, background: `${statusColor}18` }}>
            {statusLabel}
          </span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '12px' }}>
          <div><span style={{ color: faint }}>Locked Bot Balance</span><div style={{ fontWeight: 700, color: '#f59e0b' }}>${bot.lockedBalance.toFixed(2)}</div></div>
          <div><span style={{ color: faint }}>Reward / Opportunity</span><div style={{ fontWeight: 700, color: '#10b981' }}>${bot.rewardAmount.toFixed(5)}</div></div>
          <div><span style={{ color: faint }}>Interval</span><div style={{ fontWeight: 700 }}>Every {bot.opportunityInterval}h</div></div>
          <div><span style={{ color: faint }}>Remaining</span><div style={{ fontWeight: 700 }}>{bot.daysRemaining}d / {bot.durationDays}d</div></div>
        </div>
      </div>
    );
  }

  function renderOpportunitySection(opp: OpportunityData, bot: BotData) {
    if (opp.status === 'open') {
      const ms = msUntil(opp.expiresAt);
      return (
        <div style={{ ...s.card, border: '1px solid rgba(16,185,129,0.4)', background: 'rgba(16,185,129,0.06)', textAlign: 'center' }}>
          <div style={{ fontSize: '12px', color: '#10b981', fontWeight: 700, marginBottom: '4px' }}>OPPORTUNITY OPEN</div>
          <div style={{ fontSize: '12px', color: muted }}>Catch within</div>
          <div style={{ fontSize: '26px', fontWeight: 800, color: '#f59e0b', marginBottom: '10px', fontVariantNumeric: 'tabular-nums' }}>
            {ms > 0 ? fmtHms(ms) : 'Expired'}
          </div>
          <button onClick={handleCatch} disabled={acting || ms <= 0} style={s.btn(!acting && ms > 0, '#10b981')}>
            {acting ? 'Catching...' : `🎯 CATCH — $${opp.rewardAmount.toFixed(5)}`}
          </button>
        </div>
      );
    }

    if (opp.status === 'reward_pending') {
      const ms = msUntil(opp.rewardDeadline);
      return (
        <div style={{ ...s.card, border: '1px solid rgba(245,158,11,0.4)', background: 'rgba(245,158,11,0.06)' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '12px', color: '#f59e0b', fontWeight: 700, marginBottom: '4px' }}>CAUGHT ✓ — REWARD PENDING</div>
            <div style={{ fontSize: '12px', color: muted }}>Transfer to Main Balance within</div>
            <div style={{ fontSize: '26px', fontWeight: 800, color: '#f59e0b', marginBottom: '4px', fontVariantNumeric: 'tabular-nums' }}>
              {ms > 0 ? fmtHms(ms) : 'Expired'}
            </div>
            <div style={{ fontSize: '20px', fontWeight: 800, color: '#10b981', marginBottom: '12px' }}>+${opp.rewardAmount.toFixed(5)}</div>
            <button onClick={handleTransferReward} disabled={acting || ms <= 0} style={s.btn(!acting && ms > 0, '#f59e0b')}>
              {acting ? 'Transferring...' : 'Transfer to Main Balance'}
            </button>
          </div>
          <SignalBlock opp={opp} />
        </div>
      );
    }

    const states: Record<string, { label: string; color: string; note: string }> = {
      missed: { label: 'MISSED', color: '#ef4444', note: 'You did not catch this opportunity in time.' },
      lost: { label: 'REWARD LOST', color: '#ef4444', note: "You didn't transfer the reward in time." },
      rewarded: { label: 'REWARDED ✓', color: '#10b981', note: `+$${opp.rewardAmount.toFixed(5)} transferred to Main Balance` },
    };
    const st = states[opp.status];
    if (!st) return null;
    return (
      <div style={{ ...s.card, border: `1px solid ${st.color}4d`, textAlign: 'center' }}>
        <div style={{ fontSize: '12px', color: st.color, fontWeight: 700 }}>{st.label}</div>
        <div style={{ fontSize: '13px', color: muted, marginTop: '4px' }}>{st.note}</div>
        {opp.status === 'rewarded' && opp.signalPair && <SignalBlock opp={opp} />}
        {renderNextCountdown(bot)}
      </div>
    );
  }

  function renderBonuses(bot: BotData) {
    return (
      <div style={s.card}>
        <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '10px' }}>Bonuses</div>

        <div style={{ fontSize: '13px', fontWeight: 700, color: '#10b981', marginBottom: '4px' }}>⭐ Perfect Bonus — +${bot.perfectBonusAmount} USDT / day</div>
        <div style={s.row}><span style={{ color: muted }}>Perfect days</span><span style={{ fontWeight: 700 }}>{bot.perfectDaysCount} / {bot.durationDays}</span></div>
        <div style={s.row}><span style={{ color: muted }}>Earned</span><span style={{ fontWeight: 700, color: '#10b981' }}>${bot.perfectBonusTotal.toFixed(2)}</span></div>
        <div style={{ fontSize: '11px', color: faint, margin: '4px 0 12px' }}>
          Catch opportunities with no gap longer than 12 hours during a day to earn +${bot.perfectBonusAmount} for that day.
        </div>

        <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '10px' }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: '#f59e0b', marginBottom: '4px' }}>🍀 Lucky Bonus — 10% of Bot Balance</div>
          <div style={s.row}><span style={{ color: muted }}>Amount at end of term</span><span style={{ fontWeight: 700, color: '#f59e0b' }}>${bot.luckyBonusAmount.toFixed(2)}</span></div>
          <div style={s.row}>
            <span style={{ color: muted }}>Status</span>
            <span style={{ fontWeight: 700, color: bot.luckyEligible ? '#10b981' : '#ef4444' }}>
              {bot.luckyEligible ? 'On track ✓' : 'Not eligible ✗'}
            </span>
          </div>
          <div style={{ fontSize: '11px', color: faint, marginTop: '4px' }}>
            {bot.luckyEligible
              ? `Earn the Perfect Bonus every day until ${fmtDateTime(bot.endsAt).slice(0, 10)} to receive 10% of your $${bot.lockedBalance.toFixed(2)} Bot Balance.`
              : 'A day without Perfect Bonus was recorded during this term, so the Lucky Bonus is no longer available for this bot.'}
          </div>
        </div>
      </div>
    );
  }

  function renderPauseSection(bot: BotData) {
    const ms = msUntil(bot.resumeAt);
    return (
      <div style={{ ...s.card, border: '1px solid rgba(245,158,11,0.4)', textAlign: 'center' }}>
        <div style={{ fontSize: '16px', fontWeight: 800, color: '#f59e0b', marginBottom: '8px' }}>⏸️ Bot Paused</div>
        <div style={{ fontSize: '13px', color: muted, marginBottom: '12px' }}>3 consecutive opportunities were missed.</div>
        {bot.resumeAt ? (
          <div>
            <div style={{ fontSize: '12px', color: faint }}>Resumes in</div>
            <div style={{ fontSize: '24px', fontWeight: 800, color: '#f59e0b', fontVariantNumeric: 'tabular-nums' }}>{ms > 0 ? fmtHms(ms) : 'Resuming…'}</div>
          </div>
        ) : (
          <button onClick={handleResume} disabled={acting} style={s.btn(!acting, '#f59e0b')}>
            {acting ? 'Resuming...' : 'Resume Bot (24h wait)'}
          </button>
        )}
      </div>
    );
  }

  function renderLockedSection() {
    return (
      <div style={{ ...s.card, border: '1px solid rgba(239,68,68,0.4)', textAlign: 'center' }}>
        <div style={{ fontSize: '16px', fontWeight: 800, color: '#ef4444', marginBottom: '8px' }}>🔒 LOCKED</div>
        <div style={{ fontSize: '13px', color: muted, marginBottom: '12px' }}>
          You missed the reward transfer deadline. The reward was lost.
        </div>
        <button onClick={handleAcknowledgeLock} disabled={acting} style={s.btn(!acting, '#3b82f6')}>
          {acting ? 'Unlocking...' : 'Continue Trading'}
        </button>
      </div>
    );
  }

  function renderPackages() {
    const bot = data!.bot;
    const upgrading = !!bot && bot.status !== 'expired';
    return (
      <div style={s.page}>
        <Header title={upgrading ? 'Upgrade Bot' : 'Choose a Bot'} subtitle={`Bot Balance: $${data!.botBalance.toFixed(2)}`} onBack={() => goTo('main')} />
        <div style={{ padding: '0 16px' }}>
          <div style={{ ...s.card, border: '1px solid rgba(239,68,68,0.35)', background: 'rgba(239,68,68,0.06)' }}>
            <div style={{ fontSize: '13px', fontWeight: 800, color: '#ef4444', marginBottom: '6px' }}>⚠️ {t('aiTrader.risk_title')}</div>
            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', lineHeight: 1.5 }}>{t('aiTrader.risk_warning')}</div>
            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', lineHeight: 1.5, marginTop: '6px' }}>{t('aiTrader.signal_risk')}</div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
              <input type="checkbox" checked={riskAccepted} onChange={e => setRiskAccepted(e.target.checked)} style={{ width: '18px', height: '18px' }} />
              {t('aiTrader.risk_accept')}
            </label>
          </div>

          {data!.packages.map(pkg => {
            const color = PKG_COLORS[pkg.key] || '#888';
            const currentPkg = upgrading ? data!.packages.find(p => p.key === bot!.packageKey) : null;
            const totalCost = currentPkg
              ? (pkg.packageFee - currentPkg.packageFee) + (pkg.requiredBalance - currentPkg.requiredBalance)
              : pkg.packageFee + pkg.requiredBalance;
            const canAfford = data!.botBalance >= totalCost;
            const isCurrentOrLower = upgrading && pkg.packageFee <= bot!.packageFee;
            const canBuy = canAfford && !isCurrentOrLower && riskAccepted;
            const luckyAmount = pkg.requiredBalance * LUCKY_RATE;

            let label: string;
            if (acting) label = 'Processing...';
            else if (isCurrentOrLower) label = 'Current / lower package';
            else if (!canAfford) label = `Need $${totalCost} Bot Balance`;
            else if (!riskAccepted) label = 'Accept the risk warning above';
            else label = upgrading ? `Upgrade to ${pkg.name} — $${totalCost}` : `Buy ${pkg.name} — $${totalCost}`;

            return (
              <div key={pkg.key} style={{ ...s.card, borderColor: `${color}33` }}>
                <div style={{ fontSize: '18px', fontWeight: 800, color, marginBottom: '10px' }}>{pkg.name}</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', fontSize: '12px', marginBottom: '10px' }}>
                  <div><span style={{ color: faint }}>Package Fee</span><div style={{ fontWeight: 700 }}>${pkg.packageFee}</div></div>
                  <div><span style={{ color: faint }}>Required Bot Balance</span><div style={{ fontWeight: 700 }}>${pkg.requiredBalance}</div></div>
                  <div><span style={{ color: faint }}>Total Entry</span><div style={{ fontWeight: 700, color }}>${pkg.packageFee + pkg.requiredBalance}</div></div>
                  <div><span style={{ color: faint }}>Duration</span><div style={{ fontWeight: 700 }}>{pkg.durationDays} days</div></div>
                  <div><span style={{ color: faint }}>Opportunity</span><div style={{ fontWeight: 700 }}>Every {pkg.opportunityIntervalHours}h</div></div>
                  <div><span style={{ color: faint }}>Reward</span><div style={{ fontWeight: 700, color: '#10b981' }}>${pkg.rewardAmount.toFixed(5)}</div></div>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '10px', padding: '10px', fontSize: '12px', marginBottom: '10px' }}>
                  <div style={{ ...s.row, fontSize: '12px' }}>
                    <span style={{ color: muted }}>⭐ Perfect Bonus</span>
                    <span style={{ fontWeight: 700, color: '#10b981' }}>+${PERFECT_BONUS} / day (up to ${PERFECT_BONUS * pkg.durationDays})</span>
                  </div>
                  <div style={{ ...s.row, fontSize: '12px', marginBottom: 0 }}>
                    <span style={{ color: muted }}>🍀 Lucky Bonus</span>
                    <span style={{ fontWeight: 700, color: '#f59e0b' }}>10% of Bot Balance = ${luckyAmount.toFixed(2)}</span>
                  </div>
                  <div style={{ fontSize: '10px', color: faint, marginTop: '6px' }}>
                    Perfect Bonus: no catch gap over 12h during a day. Lucky Bonus: Perfect Bonus every day of the term.
                  </div>
                </div>
                <button
                  onClick={() => upgrading ? handleUpgrade(pkg.key) : handlePurchase(pkg.key)}
                  disabled={!canBuy || acting}
                  style={s.btn(canBuy && !acting, color)}
                >
                  {label}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  function renderDeposit() {
    return (
      <div style={s.page}>
        <Header title="Deposit to Bot Balance" onBack={() => goTo('main')} />
        <div style={{ padding: '0 16px' }}>
          {depositInfo ? (
            <div style={s.card}>
              <div style={{ fontSize: '13px', color: muted, marginBottom: '4px' }}>Network</div>
              <div style={{ fontSize: '16px', fontWeight: 700, marginBottom: '14px' }}>TON (USDT)</div>
              <CopyField label="Wallet Address" value={depositInfo.walletAddress} />
              <CopyField label="Memo (Required!)" value={depositInfo.memo} highlight />
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', textAlign: 'center', lineHeight: 1.5 }}>
                Send USDT to this address with the exact memo. Deposits without the memo cannot be credited automatically.
                Funds appear in your Bot Balance within a few minutes.
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px', color: faint }}>Loading deposit info...</div>
          )}
        </div>
      </div>
    );
  }

  function renderGift() {
    const bot = data!.bot;
    const canSend = !!giftRecipient?.eligible && !acting && data!.botBalance >= 20;
    return (
      <div style={s.page}>
        <Header
          title="Send USDT Gift"
          subtitle={`Bot Balance: $${data!.botBalance.toFixed(2)} · Sent this month: ${bot?.giftsSentThisMonth || 0}/${bot?.giftMonthlyLimit || 0}`}
          onBack={() => goTo('main')}
        />
        <div style={{ padding: '0 16px' }}>
          <div style={s.card}>
            <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '6px' }}>Gift Amount: $20 USDT</div>
            <div style={{ fontSize: '12px', color: muted, marginBottom: '12px' }}>
              Sent from your Bot Balance to another AI Trader user's Bot Balance. 0% commission.
            </div>
            <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '8px' }}>Receiver Username</div>
            <input type="text" value={giftUsername} onChange={e => setGiftUsername(e.target.value)} placeholder="@username" autoCapitalize="off" autoCorrect="off" style={{ ...s.input, marginBottom: '10px' }} />

            {giftUsername.trim().replace(/^@/, '').length >= 3 && (
              <div style={{
                borderRadius: '10px', padding: '10px 12px', marginBottom: '12px', fontSize: '13px',
                background: giftChecking ? 'rgba(255,255,255,0.04)' : giftRecipient?.eligible ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
                border: `1px solid ${giftChecking ? 'rgba(255,255,255,0.08)' : giftRecipient?.eligible ? 'rgba(16,185,129,0.35)' : 'rgba(239,68,68,0.35)'}`,
              }}>
                {giftChecking ? (
                  <span style={{ color: muted }}>Checking user…</span>
                ) : giftRecipient?.found ? (
                  <>
                    <div style={{ fontSize: '11px', color: muted }}>Sending to</div>
                    <div style={{ fontWeight: 700 }}>{giftRecipient.displayName} <span style={{ color: muted }}>@{giftRecipient.username}</span></div>
                    {!giftRecipient.eligible && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>{giftRecipient.reason}</div>}
                  </>
                ) : (
                  <span style={{ color: '#ef4444' }}>{giftRecipient?.reason || 'User not found'}</span>
                )}
              </div>
            )}

            <button onClick={handleSendGift} disabled={!canSend} style={s.btn(canSend, '#10b981')}>
              {acting ? 'Sending...' : data!.botBalance < 20 ? 'Need $20 Bot Balance' : 'Send $20 Gift'}
            </button>
          </div>

          {giftResult && (
            <div style={{
              ...s.card, textAlign: 'center',
              border: `1px solid ${giftResult.ok ? 'rgba(16,185,129,0.4)' : 'rgba(239,68,68,0.4)'}`,
              background: giftResult.ok ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)',
            }}>
              <div style={{ fontSize: '16px', fontWeight: 800, color: giftResult.ok ? '#10b981' : '#ef4444' }}>
                {giftResult.ok ? 'Sent ✓' : 'Failed ✗'}
              </div>
              <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)', marginTop: '4px' }}>{giftResult.message}</div>
            </div>
          )}
        </div>
      </div>
    );
  }

  function renderTransfer() {
    const amt = Number(transferAmount) || 0;
    return (
      <div style={s.page}>
        <Header title="Bot → Main Balance" subtitle={`Available: $${data!.botBalance.toFixed(2)}`} onBack={() => goTo('main')} />
        <div style={{ padding: '0 16px' }}>
          <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '8px' }}>Amount (USDT)</div>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            <input type="number" value={transferAmount} onChange={e => setTransferAmount(e.target.value)} placeholder="0" style={{ ...s.input, flex: 1 }} />
            <button onClick={() => setTransferAmount(data!.botBalance.toFixed(2))} style={{ padding: '0 14px', borderRadius: '10px', border: 'none', background: 'rgba(59,130,246,0.2)', color: '#3b82f6', fontWeight: 700, cursor: 'pointer' }}>
              MAX
            </button>
          </div>
          {amt > 0 && (
            <div style={s.card}>
              <div style={s.row}><span style={{ color: muted }}>Commission</span><span style={{ color: '#10b981' }}>0%</span></div>
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '8px', display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                <span>You receive</span>
                <span style={{ fontWeight: 700, color: '#10b981' }}>${amt.toFixed(2)} USDT</span>
              </div>
            </div>
          )}
          <button onClick={handleTransferToMain} disabled={acting || amt <= 0 || amt > data!.botBalance} style={s.btn(!acting && amt > 0 && amt <= data!.botBalance, '#f59e0b')}>
            {acting ? 'Processing...' : 'Transfer to Main Balance'}
          </button>
        </div>
      </div>
    );
  }
}
